import { Controller } from '@hotwired/stimulus'

// Fired pixel responses land in ClickHouse asynchronously (via Vector) after a
// pixel fires, so a just-fired pixel can render with no response body yet even
// though it fired successfully (see FiredPixel#pending_ch_ingestion?, whose
// "still processing" row explains the delay while this waits and shows a live
// countdown via the "countdown" target). This controller lives on the
// fired-pixels turbo-frame: when it loads with at least one pixel still
// marked "processing" (data-fired-pixel-pending), it schedules a single
// reload after the server-provided delay, then leaves it alone — if it's
// still pending after that, the per-pixel row keeps saying so.
export default class extends Controller {
    static targets = ['countdown']

    static values = {
        delay: Number,
        url: String,
    }

    connect() {
        this.hasScheduled = false
        this.timeout = null
        this.countdownInterval = null
        this.onFrameLoad = () => this.scheduleRefreshIfPending()
        this.element.addEventListener('turbo:frame-load', this.onFrameLoad)
        this.scheduleRefreshIfPending()
    }

    disconnect() {
        this.clearScheduledRefresh()
        this.element.removeEventListener('turbo:frame-load', this.onFrameLoad)
    }

    scheduleRefreshIfPending() {
        if (this.hasScheduled || !this.hasPendingContent()) return

        this.hasScheduled = true
        this.startCountdown()
        this.timeout = setTimeout(() => this.refresh(), this.delayValue)
    }

    startCountdown() {
        this.remainingSeconds = Math.round(this.delayValue / 1000)
        this.updateCountdownTargets()

        this.countdownInterval = setInterval(() => {
            this.remainingSeconds = Math.max(0, this.remainingSeconds - 1)
            this.updateCountdownTargets()
            if (this.remainingSeconds === 0) this.clearCountdown()
        }, 1000)
    }

    updateCountdownTargets() {
        this.countdownTargets.forEach((target) => {
            target.textContent = this.remainingSeconds
        })
    }

    refresh() {
        this.timeout = null
        this.clearCountdown()
        this.element.removeAttribute('src')
        this.element.src = this.urlValue
    }

    hasPendingContent() {
        return this.element.querySelector('[data-fired-pixel-pending]') !== null
    }

    clearScheduledRefresh() {
        if (this.timeout) {
            clearTimeout(this.timeout)
            this.timeout = null
        }
        this.clearCountdown()
    }

    clearCountdown() {
        if (!this.countdownInterval) return

        clearInterval(this.countdownInterval)
        this.countdownInterval = null
    }
}
